import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { IProductConfigurableSelection, IProductDataV2 } from '@df/catalog/product/product';
import { DfEventService } from '@df/core/service/df-event';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/customer/customer.interfaces';
import { WishlistToggleComponentDef } from '@df/session/wishlist/wishlist-toggle.component.def';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import head from 'lodash-es/head';
import uniq from 'lodash-es/uniq';
import { ActionComponent } from '../../../../../../ui/api/action.component';
import { ProductDecorator } from '../../../../../model/product/product.decorator';
import { WishlistStatus } from '../../../../wishlist/wishlist.interface';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_ADD_EVENT_NAME,
    PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
    ProductEventData
} from '../../../product.abstract';
import { ProductService } from '../../../product.service';

@Component({
    selector: 'product-listing-wishlist-toggle',
    templateUrl: './product-listing-wishlist-toggle.component.html',
    styleUrl: './product-listing-wishlist-toggle.component.scss',
    standalone: true,
    imports: [ActionComponent, CommonModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingWishlistToggleComponent extends WishlistToggleComponentDef implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    set product(value: ProductDecorator<IProductDataV2>) {
        this.productId = value.id;
        this._product = value;
    }

    get product(): ProductDecorator<IProductDataV2> {
        return this._product;
    }

    protected _product!: ProductDecorator<IProductDataV2>;

    @Input()
    selection?: IProductConfigurableSelection;

    @Input()
    listPosition?: number;

    remove: () => void;
    isLoading = false;

    private _wishlistCloseTimeout?: number;

    protected dfEventService = inject(DfEventService);
    protected renderer = inject(Renderer2);
    protected attributeLabelPipeService = inject(AttributeLabelPipeService);
    protected docRef = inject(DOCUMENT);
    protected elementRef = inject(ElementRef);
    protected productService = inject(ProductService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    constructor() {
        super();

        this.remove = this.disposableTimeoutService.throttle(() => this._remove(), 300);

        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    override ngOnInit() {
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {
                    if (event.id === PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME) {
                        this.onOpenEvent(event.data);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event.data);
                    }

                    if (event.id === PRODUCT_WISHLIST_ADD_EVENT_NAME) {
                        this.onAddEvent(event.data);
                    }
                }
            })
        );

        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        clearTimeout(this._wishlistCloseTimeout);
        this._wishlistCloseTimeout = undefined;

        super.ngOnDestroy();
    }

    /**
     * Checks if item is in wishlist already
     */
    override get inWishlist(): boolean {
        return this.isLoading || this.hasAnyInWishlist || super.inWishlist;
    }

    get hasAnyInWishlist(): boolean {
        if (this.listPosition === undefined) {
            return false;
        }

        return !!this.product.parent?.directChildrenIds?.find(product => (this.wishlist.getItemsByProduct(product) || []).length > 0);
    }

    /**
     * Open modal
     */
    handleClick() {
        this.isLoading = false;

        this.status.busy = true;

        const missingAttrCode = this.needSelectAttribute();

        if (!this.inWishlist) {
            if (missingAttrCode) {
                if (missingAttrCode === 'size') {
                    this.isLoading = true;

                    this.dfEventService.dispatch({
                        id: PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
                        data: {
                            product: this.product,
                            index: this.listPosition,
                            status: WishlistStatus.BeforeAdd
                        },
                        action: () => {
                            clearTimeout(this._wishlistCloseTimeout);
                            this._wishlistCloseTimeout = undefined;
                        }
                    });
                } else {
                    this.status.error = `Please select ${this.attributeLabelPipeService.execute(missingAttrCode)}`;
                }
            } else {
                this.isLoading = false;

                this.toggle();
            }
        } else {
            this.remove();
        }
    }

    protected override _toggle(): Promise<Customer.ILoadResponse> | void {
        const product = (this.item?.product || this.product) as ProductDecorator<IProductDataV2>;

        return this.wishlist.toggle(this.status, product.id, undefined, () => {
            this.dfEventService.dispatch({
                id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                data: {
                    product,
                    index: this.listPosition,
                    status: WishlistStatus.AddSuccess
                },
                action: () => {
                    this.reset();
                }
            });
        });
    }

    protected onOpenEvent(data: ProductEventData) {
        this.isLoading = true;

        this.status.busy = !!data.loading;
    }

    protected onCloseEvent(data: ProductEventData) {
        this.isLoading = false;

        this.status.reset();
    }

    protected onAddEvent(data: ProductEventData) {
        if (data.option) {
            this.wishlist.toggle(this.status, data.option.product.id, undefined, () => {
                this.dfEventService.dispatch({
                    id: PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
                    data: {
                        ...data,
                        status: WishlistStatus.AddSuccess
                    },
                    action: () => {
                        this.reset();
                    }
                });
            });
        } else {
            this.isLoading = false;
            this.status.reset();
        }
    }

    /**
     * Remove items from wishlist
     */
    protected _remove() {
        const itemId = head(this.wishlist.getItemsByProduct(this.product.id))?.data?.id;
        const items = itemId ? [itemId] : [];

        this.product.parent?.directChildrenIds?.map(id => {
            const _items = this.wishlist.getItemsByProduct(id)?.map(item => item.data.id);

            if (_items?.length) {
                items.push(..._items);
            }
        });

        this.wishlist.remove(this.status, uniq(items), () => {
            this.reset();
        });
    }

    protected needSelectAttribute(): string | undefined {
        const selection = this.selection || {};

        if (this.product?.isConfigurable && !!Object.keys(selection).length) {
            return this.product.configurableAttributeCodes?.find(i => !selection[i]);
        }

        return undefined;
    }

    protected reset() {
        clearTimeout(this._wishlistCloseTimeout);
        this._wishlistCloseTimeout = undefined;

        this.status.reset();

        this._wishlistCloseTimeout = this.timeoutService.setTimeout(() => {
            this.dfEventService.dispatch({
                id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                data: {
                    product: this.product,
                    index: this.listPosition
                }
            });
        }, 5000);
    }
}
